import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ShieldCheck, Wrench, Handshake, Star } from "lucide-react";
import { MaskLine, Reveal, ClipImage } from "../components/Motion";
import { CarCard } from "../components/CarCard";
import { Services } from "../components/Services";
import { Reviews } from "../components/Reviews";
import { api, fileUrl } from "../lib/api";
import { useContent } from "../context/ContentContext";

const HERO = "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SHOWROOM = "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const KEYS = "https://images.unsplash.com/photo-1653565217811-85b41bcd1edb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const CHAPTERS_DEFAULT = [
  { n: "01", t: "80 jaar in Vinkeveen", d: "Al ruim 80 jaar gevestigd op dezelfde vertrouwde locatie aan de Herenweg, en meer dan 33 jaar erkend Ford-(service)dealer. Een klein bedrijf met grote kwaliteiten." },
  { n: "02", t: "De klant is koning", d: "Bij ons staat u centraal. We werken met korte lijnen, persoonlijke aandacht voor iedere klant en elke auto, en een eerlijk advies zonder verkooppraatjes." },
  { n: "03", t: "Alles onder één dak", d: "Verkoop, onderhoud, APK, schadeherstel (schadegarant reparateur) én een bediend AVIA-tankstation. Uw Ford blijft bij ons in vertrouwde handen." },
];

const USP_ICONS = [ShieldCheck, Wrench, Handshake, Star];
const USPS_DEFAULT = [
  { t: "Erkend Ford-dealer", d: "33 jaar Ford-specialist" },
  { t: "Eigen werkplaats", d: "Onderhoud, APK & schade" },
  { t: "Inruil mogelijk", d: "Eerlijke inruilwaarde" },
  { t: "80 jaar in Vinkeveen", d: "Vertrouwd familiebedrijf" },
];
const MARQUEE_DEFAULT = ["Ford-dealer", "Occasions", "Onderhoud", "APK", "Schadeherstel", "Onderdelen", "AVIA-tankstation", "Inruil"];

export default function Home() {
  const { get } = useContent();
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const chapters = get("home.manifesto.chapters", CHAPTERS_DEFAULT);
  const usps = get("home.usps", USPS_DEFAULT);
  const marquee = get("home.marquee", MARQUEE_DEFAULT);

  useEffect(() => {
    api.get("/cars", { params: { uitgelicht: true } }).then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const onMove = (e) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -6, y: px * 8 });
  };

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden pt-24 pb-16" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-[#eef3f1]" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                className="text-xs font-bold tracking-[0.25em] uppercase text-primary mb-6"
              >
                {get("home.hero.eyebrow", "Automobielbedrijf Stoof — Vinkeveen")}
              </motion.p>
              <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-text-main text-5xl sm:text-6xl lg:text-[5.2rem]">
                <MaskLine delay={0.15}>{get("home.hero.title1", "Uw volgende")}</MaskLine>
                <MaskLine delay={0.28}>{get("home.hero.title2", "auto begint")}</MaskLine>
                <MaskLine delay={0.41}><span className="text-primary">{get("home.hero.title3", "hier.")}</span></MaskLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
                className="mt-8 text-base sm:text-lg text-text-muted max-w-md leading-relaxed"
              >
                {get("home.hero.intro", "Erkend Ford-dealer in Vinkeveen, al 80 jaar. Betrouwbare occasions, vakkundig onderhoud en persoonlijke service — vind de auto die bij u past.")}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <Link to="/occasions" data-testid="hero-cta-occasions" className="group inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-4 hover:bg-primary-light transition-colors">
                  {get("home.hero.cta1", "Bekijk occasions")}
                  <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-text-main/20 font-semibold px-7 py-4 hover:border-primary hover:text-primary transition-colors">
                  {get("home.hero.cta2", "Proefrit inplannen")}
                </Link>
              </motion.div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2" style={{ perspective: 1200 }}>
              <motion.div
                ref={imgRef}
                onMouseMove={onMove}
                onMouseLeave={() => setTilt({ x: 0, y: 0 })}
                style={{ y }}
                className="relative"
              >
                <motion.div
                  animate={{ rotateX: tilt.x, rotateY: tilt.y }}
                  transition={{ type: "spring", stiffness: 120, damping: 15 }}
                  style={{ transformStyle: "preserve-3d" }}
                  className="overflow-hidden"
                >
                  <motion.img
                    src={fileUrl(get("afbeeldingen.home_hero", HERO))}
                    alt="Auto in de showroom"
                    style={{ scale }}
                    initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
                    animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
                    transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1], delay: 0.35 }}
                    className="w-full aspect-[4/3] object-cover"
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-primary py-6 overflow-hidden" data-testid="marquee">
        <Marquee speed={40} gradient={false}>
          {marquee.concat(marquee).map((w, i) => (
            <span key={i} className="font-display font-bold text-2xl sm:text-3xl text-white/90 uppercase tracking-tight mx-12 flex items-center gap-12">
              {w} <span className="text-accent">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* USPs */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {usps.map((u, i) => {
            const Icon = USP_ICONS[i] || ShieldCheck;
            return (
              <Reveal key={i} delay={i * 0.06} className="bg-background p-8">
                <Icon size={28} className="text-primary" strokeWidth={1.6} />
                <h3 className="font-display font-bold text-lg mt-5">{u.t}</h3>
                <p className="text-text-muted text-sm mt-1">{u.d}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="manifesto">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary">{get("home.manifesto.eyebrow", "Waar wij voor staan")}</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-5xl mt-4 max-w-2xl leading-[1.05]">
            {get("home.manifesto.title", "Meer dan auto's verkopen. Wij bouwen aan vertrouwen.")}
          </h2>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-7 space-y-16">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="grid grid-cols-[auto_1fr] gap-6 sm:gap-10 items-start border-t border-border pt-8">
                  <span className="font-display font-black text-4xl sm:text-6xl text-accent leading-none">{c.n}</span>
                  <div>
                    <h3 className="font-display font-bold text-2xl text-text-main">{c.t}</h3>
                    <p className="text-text-muted mt-3 leading-relaxed max-w-lg">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-5 space-y-6">
            <ClipImage src={fileUrl(get("afbeeldingen.home_showroom", SHOWROOM))} alt="Showroom" className="aspect-[3/4]" />
            <ClipImage src={fileUrl(get("afbeeldingen.home_keys", KEYS))} alt="Sleuteloverdracht" className="aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <Services />

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="featured-section">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary">{get("home.featured.eyebrow", "Uitgelicht")}</p>
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-5xl mt-3">{get("home.featured.title", "Onze aanraders")}</h2>
            </div>
            <Link to="/occasions" className="group inline-flex items-center gap-2 font-semibold text-primary">
              {get("home.featured.link", "Alle occasions")}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <Reviews />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <Reveal>
          <div className="relative overflow-hidden bg-primary text-white grain px-8 sm:px-16 py-20 text-center">
            <div className="relative z-10">
              <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight uppercase max-w-3xl mx-auto leading-[0.95]">
                {get("home.cta.title", "Klaar voor een proefrit?")}
              </h2>
              <p className="mt-6 text-white/70 max-w-xl mx-auto text-lg">
                {get("home.cta.text", "Kom langs in Vinkeveen of plan online een afspraak. Wij staan voor u klaar.")}
              </p>
              <Link to="/contact" className="mt-10 inline-flex items-center gap-2 rounded-full bg-accent text-primary font-bold px-8 py-4 hover:bg-white transition-colors group">
                {get("home.cta.button", "Maak een afspraak")}
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
